import { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import ReactECharts from "echarts-for-react";
import RotateDevice from "../../components/RotateDevice/RotateDevice";
import Lf23Loader from "../../components/Lf23Loader";
import SelettorePeriodo, { annoDi, indiceMeseDi } from "./SelettorePeriodo";
import {
    API_URL, authHeaders, nf, pct, pctTxt, km, titolo, quartiere, formatData,
    FullscreenBtn, ExportBtn, AvvisoEsecuzioni, KpiCard, ico, FiltroMulti, valoriDi, opzioniDa,
} from "./comuni";

/**
 * Tab 4 — Reportistica Spazzamenti.
 *
 * Sostituisce la reportistica che il cliente tiene a mano su Excel. Struttura:
 *   filtri (mese + settimana) -> KPI -> le due matrici (km | tratte) ->
 *   tabella di dettaglio per percorso -> trend fascia + copertura quartiere,
 *   ognuno con la sua tabella -> tratte da recuperare.
 *
 * Sorgente: GET /api/gestione-percorsi/report, che aggrega in un colpo solo i percorsi
 * operativi di Interacta (community 122), l'anagrafica delle tratte (120) e, dal DB
 * dell'app degli operatori, le tratte segnate come fatte e le foto scattate.
 * Il backend NON manda percentuali: manda celle [previste, eseguite, m previsti,
 * m spazzati, recuperate, m recuperati] e le percentuali si derivano qui, cosi' non
 * esistono due versioni dello stesso numero che possono divergere.
 *
 * TRE LETTURE (selettore in testa alla pagina): Totale = fatto nel percorso + recuperato
 * dopo con un giro di recupero; Senza recuperi = solo il fatto nel percorso; Solo recuperi
 * = la quota recuperata, in punti sul previsto. Totale = Senza + Solo, sempre. Nel Totale
 * i valori che includono recuperi hanno la "linguetta rossa" (angolo in alto a destra) e
 * il tooltip dice quanto valevano prima del recupero.
 *
 * Il file e' diviso in due: `Reportistica` e' la pagina vera e da' per scontato di
 * avere i dati; `TabReportistica`, in fondo, e' il guscio che li carica e gestisce
 * caricamento, errore e storico vuoto.
 *
 * Il QUARTIERE non esiste sull'anagrafica tratti (l'abbiamo popolata noi da un file del
 * cliente che non lo conteneva): i blocchi che lo usano restano in pagina ma dichiarati
 * non disponibili, e si riaccendono da soli quando il backend inizia a mandarlo.
 */

/* Ogni cella della matrice e' un array compatto di 6 interi. Le percentuali NON
   arrivano dal backend: si derivano qui, cosi' non esistono due versioni dello
   stesso numero che possono divergere. */
const I_PRO = 0;   // tratte previste
const I_ESE = 1;   // tratte eseguite nel percorso
const I_MPRO = 2;  // metri previsti
const I_MESE = 3;  // metri spazzati nel percorso
const I_REC = 4;   // tratte recuperate dopo, con un giro di recupero
const I_MREC = 5;  // metri recuperati

/* Le tre letture, nell'ordine dei bottoni. `aiuto` e' il title del bottone. */
const MODI = [
    { key: "totale", label: "Totale", aiuto: "Copertura finale: fatto nel percorso più i recuperi. La linguetta rossa segna i valori che includono recuperi." },
    { key: "senza", label: "Senza recuperi", aiuto: "Solo quanto fatto nel percorso, senza i giri di recupero." },
    { key: "solo", label: "Solo recuperi", aiuto: "Quanto è stato recuperato con i giri di recupero, in punti percentuali sul previsto." },
];
const MODO_DEFAULT = "totale";
/** Il campo "recuperato" gemello di un campo "eseguito" (tratte o metri). */
const gemelloRec = (campoEse) => (campoEse === I_MESE ? I_MREC : I_REC);
/** Il numeratore di una cella nella lettura scelta. */
const fattoDi = (c, campoEse, modo) => {
    if (!c) return 0;
    const ese = c[campoEse] || 0;
    const rec = c[gemelloRec(campoEse)] || 0;
    if (modo === "senza") return ese;
    if (modo === "solo") return rec;
    return ese + rec;
};
/** La cella include recuperi? (e' la condizione della linguetta rossa) */
const haRecuperi = (c, campoEse) => !!c && (c[gemelloRec(campoEse)] || 0) > 0;




/* Le settimane dei mesi compresi fra due indici, in ordine e SENZA ripetizioni:
   una settimana a cavallo appartiene a due mesi (vedi il backend), e prendendoli
   entrambi arriverebbe due volte. */
const settimaneFra = (mesi, anno, [da, a]) => {
    const ids = mesi
        .filter((m) => annoDi(m.id) === anno && indiceMeseDi(m.id) >= da && indiceMeseDi(m.id) <= a)
        .flatMap((m) => m.settimane);
    return [...new Set(ids)].sort();
};

// Somma celle campo per campo; ignora i null. Un campo assente vale 0.
const CELLA_ZERO = [0, 0, 0, 0, 0, 0];
const sommaCelle = (celle) =>
    celle.reduce((acc, c) => (c ? acc.map((v, k) => v + (c[k] || 0)) : acc), CELLA_ZERO);

/* Aggregati per fascia e per quartiere: si leggono dagli aggregati che il backend
   calcola sui TRATTI veri, non sommando le righe della matrice. E' la differenza che
   fa tornare i totali con i fogli del cliente (una codifica puo' attraversare piu'
   quartieri). Funzioni di modulo, non del componente: cosi' non finiscono nei
   dependency array dei useMemo che le usano. */
const aggregato = (settimane, chiave, elenco, ids) =>
    Object.fromEntries(elenco.map((k) => [k, sommaCelle(ids.map((id) => settimane[id][chiave][k] || null))]));

const aggregatoFoto = (settimane, chiave, elenco, ids) =>
    Object.fromEntries(elenco.map((k) => [k, ids.reduce((a, id) => a + ((settimane[id][chiave] || {})[k] || 0), 0)]));


/* ------------------------------------------------------------------ *
 * Scala colore delle matrici: gradiente CONTINUO dallo 0 al 100, senza
 * scalini. Il colore di una cella e' interpolato fra le tappe qui sotto,
 * posizionate in modo che il giallo cada nella fascia 70-90 e il verde
 * entri sopra il 90, come concordato col cliente.
 * ------------------------------------------------------------------ */
const TAPPE = [
    [0, [127, 29, 29]],     // rosso scuro
    [35, [220, 38, 38]],    // rosso
    [55, [237, 110, 58]],   // rosso-arancio
    [70, [245, 158, 11]],   // ambra — inizio della fascia gialla
    [85, [253, 224, 71]],   // giallo
    [92, [163, 230, 53]],   // lime — il verde entra sopra il 90
    [100, [21, 128, 61]],   // verde scuro
];

/** Colore della scala al valore `v` (0-100), interpolato fra le tappe. */
const interpola = (v) => {
    const x = Math.min(100, Math.max(0, v));
    let i = 0;
    while (i < TAPPE.length - 2 && x > TAPPE[i + 1][0]) i += 1;
    const [x0, c0] = TAPPE[i];
    const [x1, c1] = TAPPE[i + 1];
    const t = x1 === x0 ? 0 : (x - x0) / (x1 - x0);
    return c0.map((c, k) => Math.round(c + (c1[k] - c) * t));
};
const rgb = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;
const schiarisci = (c, t) => c.map((x) => Math.round(x + (255 - x) * t));

/** Gradiente della barra di legenda: le stesse tappe, in orizzontale. */
const LEGENDA_GRADIENTE = `linear-gradient(90deg, ${TAPPE.map(([x, c]) => `${rgb(c)} ${x}%`).join(", ")})`;

/**
 * Fondo e colore del testo di una cella. Il fondo e' a sua volta sfumato
 * (135°, come i badge del progetto): stessa tinta, un filo piu' chiara in alto.
 */
const coloreCella = (v) => {
    const c = interpola(v);
    const lum = (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255;
    return {
        background: `linear-gradient(135deg, ${rgb(schiarisci(c, 0.16))}, ${rgb(c)})`,
        color: lum > 0.55 ? "#1A202C" : "#FFFFFF",
    };
};

/* Solo recuperi: valori piccoli (pochi punti), che sulla scala 0-100 sembrerebbero tutti
   "rossi". Scala propria, neutra a 0 e piena dal 25%: dice "quanto si e' recuperato",
   non "quanto e' andata male". */
const PIENO_SOLO = 25;
const coloreSolo = (v) => {
    const t = Math.min(1, Math.max(0, (v || 0) / PIENO_SOLO));
    const da = [241, 245, 249];
    const a = [124, 58, 237];
    const c = da.map((x, k) => Math.round(x + (a[k] - x) * t));
    return {
        background: `linear-gradient(135deg, ${rgb(schiarisci(c, 0.16))}, ${rgb(c)})`,
        color: t > 0.5 ? "#FFFFFF" : "#334155",
    };
};
const LEGENDA_SOLO = `linear-gradient(90deg, ${rgb([241, 245, 249])} 0%, ${rgb([124, 58, 237])} 100%)`;

// Palette delle serie: variabili LF23 gia' in App.css (primary-blue, apps-orange-dark, ai-purple).
const CAT = ["#2E6DB1", "#EA580C", "#9333EA"];
const INK2 = "#475569";
const TOOLTIP = {
    backgroundColor: "rgba(15, 23, 42, 0.95)", borderColor: "transparent", borderRadius: 10,
    padding: [10, 14], textStyle: { color: "#F8FAFC", fontSize: 12, fontWeight: 500 },
    extraCssText: "box-shadow: 0 10px 25px rgba(0,0,0,0.25); backdrop-filter: blur(8px);",
};
const LEGEND = { icon: "circle", itemWidth: 10, itemHeight: 10, itemGap: 14, textStyle: { fontSize: 11, color: INK2, fontWeight: 500 } };
const AXIS_LABEL = { color: INK2, fontSize: 11 };
const SPLIT_LINE = { lineStyle: { color: "#EEF2F6", type: "solid" } };

/* Ampiezza minima dell'asse del trend. Un asse che si stringe troppo ingigantisce
   differenze che non contano: tre fasce a 88, 89 e 90% su una scala 85-92 finiscono
   agli antipodi del grafico e sembrano lontanissime. Venti punti percentuali sono il
   respiro sotto il quale non si scende. */
const AMPIEZZA_MIN = 20;
const conPavimento = (min, max) => {
    // Solo allargare, mai stringere: un dominio gia' largo (0-100) va lasciato stare.
    if (max - min >= AMPIEZZA_MIN) return { min, max };
    /* Si alza prima il tetto, fermandolo a 100, e solo dopo si ricava il minimo:
       facendo il contrario una serie tutta sopra il 95% resterebbe schiacciata in cima. */
    const alto = Math.min(100, min + AMPIEZZA_MIN);
    return { min: Math.max(0, alto - AMPIEZZA_MIN), max: alto };
};

/** Sparkline: andamento della copertura sulle settimane della finestra di trend. */
/* `min` e' il fondo scala, comune a tutta la colonna perche' le sparkline delle
   varie righe vanno confrontate fra loro. Non e' fisso a 50: i valori sotto il fondo
   verrebbero schiacciati sul fondo stesso, e un percorso allo 0% risulterebbe
   indistinguibile da uno al 50%. */
const Sparkline = ({ valori, min = 50 }) => {
    if (!valori.length) return <span className="muted">—</span>;
    const W = 66, H = 20, MIN = min, MAX = 100;
    const punti = valori.map((v, i) => {
        const x = valori.length === 1 ? W / 2 : (i / (valori.length - 1)) * (W - 2) + 1;
        const y = H - 2 - ((Math.min(MAX, Math.max(MIN, v)) - MIN) / (MAX - MIN)) * (H - 4);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const [lx, ly] = punti[punti.length - 1].split(",");
    return (
        <svg className="gp-rep-spark" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img"
            aria-label={`andamento: ${valori.map((v) => `${Math.round(v)}%`).join(", ")}`}>
            <polyline points={punti.join(" ")} fill="none" stroke={CAT[0]} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={lx} cy={ly} r="2.4" fill={CAT[0]} stroke="#fff" strokeWidth="1.2" />
        </svg>
    );
};

/**
 * Una delle due matrici percorso x giorno.
 * `campoPro`/`campoEse` scelgono cosa misurare: tratte oppure metri.
 * L'evidenziazione (riga/colonna) e' condivisa fra le due matrici: sono due viste
 * della stessa griglia, quindi cliccare "MAR" a sinistra evidenzia "MAR" anche a destra.
 */
const Matrice = ({ titolo: intestazione, sottotitolo, righe, giorni, campoPro, campoEse, unita, hl, onHl, modo }) => {
    const attivo = hl.riga !== null || hl.colonna !== null;
    // Con riga e colonna entrambe selezionate si evidenzia la CROCE (unione),
    // come in un foglio di calcolo: non il solo incrocio.
    const evidenziata = (cod, giorno) => !attivo || hl.riga === cod || hl.colonna === giorno;

    /* Tooltip custom, stesso pattern del badge di discordanza in ManutenzioneAutomezzi.
       Due motivi per non usare title=":
        - il tooltip nativo compare dopo ~1s e il ritardo non e' configurabile;
        - un tooltip in CSS verrebbe tagliato da .table-scroll { overflow-x: auto }.
       Un solo elemento in position: fixed, e un solo handler sulla tabella invece di
       centosettanta: il testo di ogni cella viaggia nel suo data-tip. */
    const [tip, setTip] = useState(null);
    const mostraTip = (e) => {
        const td = e.target.closest?.("td[data-tip]");
        if (!td) return setTip(null);
        const r = td.getBoundingClientRect();
        const sotto = r.top < 60;   // troppo in alto: il tooltip si ribalta sotto la cella
        setTip({
            text: td.dataset.tip,
            // clamp orizzontale, per le colonne ai bordi dello schermo
            x: Math.min(Math.max(r.left + r.width / 2, 140), window.innerWidth - 140),
            y: sotto ? r.bottom : r.top,
            sotto,
        });
    };

    /* Totale per colonna: come il totale di riga, si sommano i valori grezzi del
       giorno su tutti i percorsi visibili e la percentuale si fa una volta sola,
       quindi e' pesata sui km/tratte e NON e' una media delle percentuali (concordato
       col cliente). L'angolo in basso a destra torna cosi' identico al KPI in alto. */
    const totColonne = giorni.map((g) => sommaCelle(righe.map((r) => r.giorni[g])));
    const totGenerale = sommaCelle(righe.map((r) => r.tot));
    /* Testo del tooltip, nella lettura scelta. Nel Totale, se la cella include recuperi,
       dice anche il "prima" e il "dopo": e' il dettaglio della linguetta rossa. */
    const quanti = (c, campo) => (unita === "km" ? `${nf(km(c[campo] || 0), 2)} km` : `${nf(c[campo] || 0)} tratt${(c[campo] || 0) === 1 ? "a" : "e"}`);
    const dettaglioDi = (c) => {
        const fatto = fattoDi(c, campoEse, modo);
        const base = unita === "km"
            ? `${nf(km(fatto), 2)} di ${nf(km(c[I_MPRO]), 2)} km`
            : `${fatto} di ${c[I_PRO]} tratte`;
        if (modo === "solo") return `${base} recuperat${unita === "km" ? "i" : "e"}`;
        if (modo !== "totale" || !haRecuperi(c, campoEse)) return base;
        const prima = pct(c[campoEse] || 0, c[campoPro]);
        const dopo = pct(fatto, c[campoPro]);
        return `${base} · prima del recupero ${nf(prima, 1)}% → dopo ${nf(dopo, 1)}% (+${quanti(c, gemelloRec(campoEse))} recuperat${unita === "km" ? "i" : "e"})`;
    };
    const valoreDi = (c) => pct(fattoDi(c, campoEse, modo), c[campoPro]);
    const colore = (v) => (modo === "solo" ? coloreSolo(v) : coloreCella(v));
    // Linguetta rossa: solo nel Totale e solo dove c'e' davvero un recupero.
    const flag = (c) => (modo === "totale" && haRecuperi(c, campoEse) ? " gp-rec-flag" : "");
    const pctGenerale = valoreDi(totGenerale);

    return (
        <div className="gp-rep-card">
            <div className="gp-rep-card-head">
                <div>
                    <div className="gp-rep-title">{intestazione}</div>
                    <div className="gp-rep-sub">{sottotitolo}</div>
                </div>
            </div>
            <div className="table-scroll">
                <table className="gp-mx" onMouseOver={mostraTip} onMouseLeave={() => setTip(null)}>
                    <thead>
                        <tr>
                            <th className="gp-mx-corner" />
                            {giorni.map((g) => (
                                <th key={g}>
                                    <button
                                        className={`gp-mx-head ${hl.colonna === g ? "is-on" : ""}`}
                                        onClick={() => onHl({ riga: hl.riga, colonna: hl.colonna === g ? null : g })}
                                        title={`Evidenzia ${g}`}
                                    >
                                        {g}
                                    </button>
                                </th>
                            ))}
                            <th className="gp-mx-tot">Tot.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {righe.map((r) => {
                            const totPct = valoreDi(r.tot);
                            const totDettaglio = dettaglioDi(r.tot);
                            return (
                                <tr key={r.cod}>
                                    <th scope="row" className="gp-mx-rowhead">
                                        <button
                                            className={`gp-mx-head ${hl.riga === r.cod ? "is-on" : ""}`}
                                            onClick={() => onHl({ riga: hl.riga === r.cod ? null : r.cod, colonna: hl.colonna })}
                                            title={`Evidenzia ${r.cod}`}
                                        >
                                            {r.cod}
                                            <span className="gp-mx-fascia">{titolo(r.fascia)}</span>
                                        </button>
                                    </th>
                                    {giorni.map((g) => {
                                        const c = r.giorni[g];
                                        if (!c || !c[campoPro]) {
                                            /* Due vuoti diversi: il percorso non era in programma, oppure
                                               c'e' ma non e' ancora finito (il giro di stasera, quello di
                                               domani). Confonderli farebbe leggere la settimana in corso
                                               come piena di giri saltati. */
                                            const attesa = (r.attesa || []).includes(g);
                                            // aria-label perche' queste celle non hanno testo da leggere
                                            const testo = attesa
                                                ? `${r.cod} · ${g} — percorso previsto, non ancora concluso`
                                                : `${r.cod} · ${g} — percorso non previsto in questo giorno`;
                                            return (
                                                <td key={g} data-tip={testo} aria-label={testo}
                                                    className={`gp-mx-cell ${attesa ? "is-attesa" : "is-vuota"}`}>
                                                    {attesa ? "—" : null}
                                                </td>
                                            );
                                        }
                                        const v = valoreDi(c);
                                        const dettaglio = dettaglioDi(c);
                                        const testo = `${r.cod}${r.percorsiDelGiorno?.[g] ? ` (${r.percorsiDelGiorno[g].join(", ")})` : ""} · ${g} — ${dettaglio} (${nf(v, 1)}%)`;
                                        return (
                                            <td
                                                key={g}
                                                className={`gp-mx-cell ${evidenziata(r.cod, g) ? "" : "is-dim"}${flag(c)}`}
                                                style={colore(v)}
                                                data-tip={testo}
                                                aria-label={flag(c) ? `${testo} — include recuperi` : undefined}
                                            >
                                                {nf(v, 0)}%
                                            </td>
                                        );
                                    })}
                                    <td className={`gp-mx-cell gp-mx-tot ${!attivo || hl.riga === r.cod ? "" : "is-dim"}${flag(r.tot)}`}
                                        data-tip={totPct === null
                                            ? `${r.cod} · nessun passaggio concluso nel periodo`
                                            : `${r.cod} · totale periodo — ${totDettaglio} (${nf(totPct, 1)}%)`}>
                                        {totPct === null ? "—" : `${nf(totPct, 0)}%`}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    {righe.length > 0 && (
                        <tfoot>
                            <tr className="gp-mx-foot">
                                <th scope="row" className="gp-mx-rowhead gp-mx-foot-label">Totale</th>
                                {giorni.map((g, i) => {
                                    const c = totColonne[i];
                                    const v = valoreDi(c);
                                    return (
                                        <td key={g}
                                            className={`gp-mx-cell gp-mx-tot ${!attivo || hl.colonna === g ? "" : "is-dim"}${flag(c)}`}
                                            data-tip={v === null
                                                ? `${g} · nessun passaggio concluso nel periodo`
                                                : `${g} · totale giorno — ${dettaglioDi(c)} (${nf(v, 1)}%)`}>
                                            {v === null ? "—" : `${nf(v, 0)}%`}
                                        </td>
                                    );
                                })}
                                <td className={`gp-mx-cell gp-mx-tot gp-mx-tot-generale${flag(totGenerale)}`}
                                    data-tip={pctGenerale === null
                                        ? "Nessun passaggio concluso nel periodo"
                                        : `Totale periodo — ${dettaglioDi(totGenerale)} (${nf(pctGenerale, 1)}%)`}>
                                    {pctGenerale === null ? "—" : `${nf(pctGenerale, 0)}%`}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
            {tip && (
                <div className={`gp-mx-tip${tip.sotto ? " sotto" : ""}`} role="tooltip"
                    style={{ left: tip.x, top: tip.y }}>
                    {tip.text}
                </div>
            )}
        </div>
    );
};

/**
 * Il corpo della pagina. Riceve il report gia' caricato, quindi puo' dare per scontato
 * che ci sia almeno un mese e una settimana: e' il guscio TabReportistica, in fondo al
 * file, a occuparsi di caricamento, errore e storico vuoto.
 *
 * Le costanti si destrutturano con i nomi maiuscoli che avevano quando erano import
 * del mock: sotto, i calcoli restano quelli collaudati.
 */
const Reportistica = ({ dati, onAggiorna }) => {
    const {
        giorni: GIORNI, fasce: FASCE, quartieri: QUARTIERI,
        mesi: MESI, settimane: SETTIMANE, recuperi, meta,
    } = dati;
    const CRITICHE_TOT = recuperi.totali;
    const CRITICHE_ROSSE = recuperi.rosse;
    // Il quartiere non e' sull'anagrafica tratti: finche' non arriva, i blocchi che lo
    // usano restano in pagina ma dichiarati non disponibili (vedi piu' sotto).
    const conQuartieri = QUARTIERI.length > 0;

    /* ------------------------- filtri e periodo -------------------------
       Tutto il pannello — periodo compreso — segue la regola "bozza + Applica":
       si tocca quello che si vuole e la pagina cambia solo quando lo si conferma.
       Il periodo prima agiva subito; ora e' un filtro come gli altri.

       `query` e' la FOTOGRAFIA applicata, `f*` la bozza dentro il pannello. Chiudendo
       il pannello senza applicare la bozza si perde: alla riapertura torna a
       combaciare con la fotografia (vedi l'effetto piu' sotto), cosi' non si resta
       con dei campi che dicono una cosa e una pagina che ne mostra un'altra. */

    /* Periodo di partenza: mese corrente a destra, mese precedente a sinistra, con
       accesa la SOLA ultima settimana. E' quello a cui si torna a ogni ricarica —
       niente viene ricordato fra una visita e l'altra.

       Due casi che vanno gestiti o la pagina si apre vuota:
        - a GENNAIO il mese precedente sta nell'anno prima, e questo slider copre un
          anno solo: si parte da gennaio e basta;
        - se l'ANNO CORRENTE non ha ancora dati (siamo a gennaio e l'ultimo dato e' di
          dicembre, oppure il servizio e' fermo) si ricade sull'ultimo anno che ne ha. */
    const periodoIniziale = useMemo(() => {
        const oggi = new Date();
        const annoOggi = oggi.getFullYear();
        const haDati = (y) => MESI.some((m) => annoDi(m.id) === y);

        let anno; let range;
        if (haDati(annoOggi)) {
            const m = oggi.getMonth();
            anno = annoOggi; range = [Math.max(0, m - 1), m];
        } else {
            const ultimo = MESI[MESI.length - 1];
            const i = indiceMeseDi(ultimo.id);
            anno = annoDi(ultimo.id); range = [Math.max(0, i - 1), i];
        }
        // Tutte spente tranne l'ultima: e' la "selezione standard" della pagina.
        const dentro = settimaneFra(MESI, anno, range);
        const spente = new Set(dentro.slice(0, -1));
        return { anno, range, spente };
    }, [MESI]);

    // Fotografia applicata.
    const [query, setQuery] = useState(() => ({
        ...periodoIniziale, fasce: [], quartieri: [], percorsi: [],
    }));
    // Bozza dentro il pannello.
    const [fAnno, setFAnno] = useState(periodoIniziale.anno);
    const [fRange, setFRange] = useState(periodoIniziale.range);
    const [fSpente, setFSpente] = useState(periodoIniziale.spente);
    const [fFascia, setFFascia] = useState([]);
    const [fQuartiere, setFQuartiere] = useState([]);
    const [fPercorso, setFPercorso] = useState([]);

    const [showFilters, setShowFilters] = useState(false);
    /* Lettura delle percentuali (Totale / Senza recuperi / Solo recuperi). Non e' un
       filtro: non cambia COSA si guarda ma COME si legge, quindi agisce subito e non
       passa dalla bozza del pannello. */
    const [modo, setModo] = useState(MODO_DEFAULT);
    const [hl, setHl] = useState({ riga: null, colonna: null });
    const [fsMatrici, setFsMatrici] = useState(false);
    const [fsTabella, setFsTabella] = useState(false);

    /* Riaprendo il pannello la bozza torna alla fotografia applicata: le modifiche
       lasciate a meta' e non confermate spariscono, invece di restare li' a suggerire
       un filtro che la pagina non sta usando. */
    useEffect(() => {
        if (!showFilters) return;
        setFAnno(query.anno);
        setFRange(query.range);
        setFSpente(new Set(query.spente));
        setFFascia(query.fasce.map((v) => ({ value: v, label: titolo(v) })));
        setFQuartiere(query.quartieri.map((v) => ({ value: v, label: quartiere(v) })));
        setFPercorso(query.percorsi.map((v) => ({ value: v, label: v })));
    }, [showFilters, query]);

    // Le settimane del periodo APPLICATO (non della bozza).
    const settimaneDelRange = useMemo(
        () => settimaneFra(MESI, query.anno, query.range),
        [MESI, query.anno, query.range]
    );

    /* --- comandi della BOZZA: toccano solo il pannello, non la pagina --- */
    const cambiaAnno = (y) => {
        setFAnno(y);
        // Stessa regola dell'apertura: l'ultimo mese con dati di quell'anno e il precedente.
        const disponibili = MESI.filter((m) => annoDi(m.id) === y).map((m) => indiceMeseDi(m.id));
        const i = disponibili.length ? Math.max(...disponibili) : fRange[1];
        setFRange([Math.max(0, i - 1), i]);
        setFSpente(new Set());   // periodo nuovo, settimane tutte accese
    };
    const cambiaRange = (r) => { setFRange(r); setFSpente(new Set()); };
    /* Spegnere l'ultima settimana accesa lascerebbe la pagina senza periodo: il
       click non fa nulla, come un radio che non si puo' deselezionare. */
    const toggleSettimana = (id) => setFSpente((s) => {
        const dentro = settimaneFra(MESI, fAnno, fRange);
        const next = new Set(s);
        if (next.has(id)) next.delete(id);
        else if (dentro.filter((x) => !s.has(x)).length > 1) next.add(id);
        return next;
    });

    /* Applica: la bozza diventa la fotografia. Da qui in poi la pagina cambia.
       Se il periodo scelto non contiene nessuna settimana ci si ferma: applicarlo
       lascerebbe la pagina senza dati e senza spiegazione. */
    const applicaFiltri = () => {
        const dentro = settimaneFra(MESI, fAnno, fRange);
        if (!dentro.length) return;
        setQuery({
            anno: fAnno,
            range: fRange,
            spente: new Set([...fSpente].filter((id) => dentro.includes(id))),
            fasce: valoriDi(fFascia), quartieri: valoriDi(fQuartiere), percorsi: valoriDi(fPercorso),
        });
        setHl({ riga: null, colonna: null });
    };

    // Reset: si torna alla selezione standard, la stessa che si trova ricaricando.
    const resetFiltri = () => {
        setFAnno(periodoIniziale.anno);
        setFRange(periodoIniziale.range);
        setFSpente(new Set(periodoIniziale.spente));
        setFFascia([]); setFQuartiere([]); setFPercorso([]);
        setQuery({ ...periodoIniziale, spente: new Set(periodoIniziale.spente), fasce: [], quartieri: [], percorsi: [] });
        setHl({ riga: null, colonna: null });
    };

    /* La settimana su cui si calcola tutto. Resta una lista di uno solo perche'
       matrici, aggregati ed evidenziazione la consumano gia' in questa forma.
       Il fallback protegge dal caso in cui la settimana selezionata non appartenga
       (piu') al mese filtrato. */
    /* Le settimane su cui si calcola tutto: quelle dell'intervallo che l'utente non
       ha spento. Se per qualche motivo restassero zero si ricade sull'intervallo
       intero, cosi' la pagina non si trova mai senza periodo. */
    const settimaneAttive = useMemo(() => {
        const on = settimaneDelRange.filter((id) => !query.spente.has(id));
        return on.length ? on : settimaneDelRange;
    }, [settimaneDelRange, query.spente]);

    /* Righe della matrice. Le righe di ogni settimana sono nello stesso ordine
       (stessa lista di codifiche), quindi si aggregano per indice. */
    const righeTutte = useMemo(() => {
        const base = SETTIMANE[settimaneAttive[0]].righe;
        return base.map((riga, i) => {
            const giorni = {};
            GIORNI.forEach((g) => {
                const celle = settimaneAttive.map((id) => SETTIMANE[id].righe[i].giorni[g]).filter(Boolean);
                giorni[g] = celle.length ? sommaCelle(celle) : null;
            });
            const conFoto = settimaneAttive.reduce((acc, id) => acc + SETTIMANE[id].righe[i].conFoto, 0);
            /* Percorso valido in almeno una delle settimane scelte (date di validita'
               dell'anagrafica, calcolate dal backend): con un periodo a cavallo dello
               switch si vedono vecchi e nuovi, dopo lo switch solo i nuovi.
               `!== false`: una risposta senza il campo non nasconde niente. */
            const attiva = settimaneAttive.some((id) => SETTIMANE[id].righe[i].attiva !== false);
            /* Riga "Bonifica" (task 541): quali percorsi hanno girato in ogni giorno, sommando le
               settimane scelte (in tre settimane di lunedi': 1-54, 1-60, 1-66). Solo per il tooltip. */
            const percorsiDelGiorno = riga.percorsiDelGiorno
                ? Object.fromEntries(GIORNI
                    .map((g) => [g, [...new Set(settimaneAttive.flatMap((id) => SETTIMANE[id].righe[i].percorsiDelGiorno?.[g] || []))]
                        .sort((a, b) => a.localeCompare(b, "it", { numeric: true }))])
                    .filter(([, l]) => l.length))
                : undefined;
            return { ...riga, giorni, tot: sommaCelle(Object.values(giorni)), conFoto, attiva, percorsiDelGiorno };
        });
    }, [GIORNI, SETTIMANE, settimaneAttive]);

    const righe = useMemo(
        () => righeTutte.filter((r) =>
            r.attiva &&
            (!query.fasce.length || query.fasce.includes(r.fascia)) &&
            (!query.quartieri.length || query.quartieri.includes(r.quartiere)) &&
            (!query.percorsi.length || query.percorsi.includes(r.cod))),
        [righeTutte, query.fasce, query.quartieri, query.percorsi]
    );

    /* Fascia/Quartiere/Percorso restringono matrici e dettaglio, NON le due tabelle
       aggregate qui sotto: quelle arrivano gia' sommate dal backend sui singoli tratti
       (una codifica puo' attraversare piu' quartieri, quindi filtrarle per riga darebbe
       numeri sbagliati). Serve a dirlo in pagina, ma solo quando un filtro e' acceso. */
    const filtriAttivi = query.fasce.length > 0 || query.quartieri.length > 0 || query.percorsi.length > 0;

    const totale = useMemo(() => sommaCelle(righe.map((r) => r.tot)), [righe]);
    const conFotoTot = useMemo(() => righe.reduce((a, r) => a + r.conFoto, 0), [righe]);

    /* I punti dell'asse X dei grafici e delle sparkline: una settimana ciascuno.
       Ogni punto porta la lista delle settimane che lo compongono — oggi sempre una —
       cosi' i grafici sommano allo stesso modo senza sapere com'e' fatto il periodo. */
    const punti = useMemo(
        () => settimaneAttive.map((id) => ({
            id, etichetta: SETTIMANE[id].num, sotto: SETTIMANE[id].giorniLabel, settimane: [id],
        })),
        [settimaneAttive, SETTIMANE]
    );

    // Copertura in chilometri per codifica, punto per punto: alimenta le sparkline.
    const trendPerCodifica = useMemo(() => {
        const out = {};
        righeTutte.forEach((r, i) => {
            out[r.cod] = punti.map((p) => {
                const c = sommaCelle(p.settimane.flatMap((id) => Object.values(SETTIMANE[id].righe[i].giorni)));
                // Copertura in CHILOMETRI: e' la misura che conta per il cliente
                // ("la percentuale va fatta nei chilometri, non in base alle vie").
                return pct(fattoDi(c, I_MESE, modo), c[I_MPRO]) || 0;
            });
        });
        return out;
    }, [SETTIMANE, righeTutte, punti, modo]);

    // Fondo scala comune delle sparkline: vedi il commento sul componente.
    const minSpark = useMemo(() => {
        const valori = Object.values(trendPerCodifica).flat();
        // Solo recuperi: valori vicini allo zero, il fondo scala e' lo zero.
        if (modo === "solo") return 0;
        if (!valori.length) return 50;
        return Math.max(0, Math.min(50, Math.floor(Math.min(...valori) / 10) * 10));
    }, [trendPerCodifica, modo]);

    const perFascia = useMemo(() => aggregato(SETTIMANE, "perFascia", FASCE, settimaneAttive), [SETTIMANE, FASCE, settimaneAttive]);
    const perQuartiere = useMemo(() => aggregato(SETTIMANE, "perQuartiere", QUARTIERI, settimaneAttive), [SETTIMANE, QUARTIERI, settimaneAttive]);

    /* Tratte eseguite con almeno una foto, per fascia e per quartiere. Il backend le
       conta sul singolo TRATTO (regola: >= 1 foto = tratta coperta, la stessa del tab 1),
       quindi qui si sommano e basta. */
    const fotoFascia = useMemo(() => aggregatoFoto(SETTIMANE, "fotoFascia", FASCE, settimaneAttive), [SETTIMANE, FASCE, settimaneAttive]);
    const fotoQuartiere = useMemo(() => aggregatoFoto(SETTIMANE, "fotoQuartiere", QUARTIERI, settimaneAttive), [SETTIMANE, QUARTIERI, settimaneAttive]);

    // Come si legge il passo del trend, in italiano e senza "1 settimane".
    const passoTxt = punti.length === 1 ? "una sola settimana" : `${punti.length} settimane`;

    // Come si legge il periodo filtrato: "Agosto 2026" oppure "S33 · 10–15 ago".
    /* Come si legge il periodo: una settimana sola col suo numero ISO, piu' settimane
       col numero della prima e dell'ultima. Il nome dei mesi lo dice gia' il selettore. */
    const etichettaPeriodo = useMemo(() => {
        if (!settimaneAttive.length) return "—";
        const a = SETTIMANE[settimaneAttive[0]];
        const b = SETTIMANE[settimaneAttive[settimaneAttive.length - 1]];
        if (settimaneAttive.length === 1) return `${a.num} \u00B7 ${a.giorniLabel}`;
        return `${a.num}–${b.num} \u00B7 ${settimaneAttive.length} settimane`;
    }, [SETTIMANE, settimaneAttive]);

    /* Export Excel di una sezione. Manda al backend le righe GIA' filtrate: il file
       esporta esattamente cio' che si sta guardando — settimana e filtri compresi — e
       il backend non deve reimplementare la logica di filtro della pagina.
       Stesso pattern di AgenteControlloRendicontazioni: POST + blob + saveAs. */
    const esporta = async (tipo, daEsportare) => {
        try {
            const res = await axios.post(
                `${API_URL}/gestione-percorsi/report/export`,
                { tipo, periodo: etichettaPeriodo, giorni: GIORNI, righe: daEsportare, modo },
                { headers: authHeaders(), responseType: "blob" }
            );
            const nome = res.headers["content-disposition"]?.match(/filename=([^;]+)/)?.[1]?.trim();
            saveAs(res.data, nome || `reportistica_${tipo}.xlsx`);
        } catch (e) {
            console.error("Errore export Excel:", e);
            alert("Non è stato possibile generare il file Excel.");
        }
    };

    /* Asse X condiviso dai due grafici. Ora i punti sono TUTTI dentro il periodo
       scelto — il grafico segue il filtro invece di mostrare otto settimane fisse —
       quindi non c'e' piu' un "contesto" da smorzare: si scrivono tutti allo stesso
       modo, etichetta sopra e dettaglio sotto. */
    const asseSettimane = useMemo(() => ({
        type: "category",
        data: punti.map((p) => `${p.etichetta}\n${p.sotto}`),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#CBD5E1" } },
        axisLabel: {
            interval: 0, lineHeight: 14,
            formatter: (v) => {
                const [num, gg] = v.split("\n");
                return `{on|${num}}\n{onGg|${gg}}`;
            },
            rich: {
                on: { color: "#2E6DB1", fontSize: 11, fontWeight: 700, lineHeight: 14 },
                onGg: { color: INK2, fontSize: 9.5, lineHeight: 13 },
            },
        },
    }), [punti]);

    /* Minimo dell'asse del trend. Fisso a 50 la parte alta respira, che e' dove
       stanno i valori quando le cose vanno bene; ma un punto sotto il minimo
       sparirebbe dal grafico senza spiegazione, quindi l'asse scende se serve. */
    const dominioTrend = useMemo(() => {
        const valori = punti.flatMap((p) => FASCE.map((f) => {
            const c = aggregato(SETTIMANE, "perFascia", [f], p.settimane)[f];
            return c && c[I_MPRO] ? pct(fattoDi(c, I_MESE, modo), c[I_MPRO]) : null;
        })).filter((v) => v !== null);
        if (!valori.length) return modo === "solo" ? { min: 0, max: 10 } : { min: 50, max: 100 };
        const basso = Math.min(...valori);
        const alto = Math.max(...valori);
        // Solo recuperi: da zero a poco sopra il massimo, altrimenti la linea sta sul fondo.
        if (modo === "solo") return { min: 0, max: Math.min(100, Math.max(10, Math.ceil(alto / 5) * 5 + 5)) };

        /* Con una sola settimana non c'e' nessun andamento da leggere: la scala larga
           serve solo a schiacciare i tre punti uno sull'altro. Ci si stringe attorno ai
           valori, cosi' le fasce si distinguono. L'asse resta etichettato, quindi si
           vede a che altezza si sta guardando. */
        if (punti.length === 1) {
            const margine = Math.max(5, (alto - basso) * 1.5);
            return conPavimento(
                Math.max(0, Math.floor((basso - margine) / 5) * 5),
                Math.min(100, Math.ceil((alto + margine) / 5) * 5)
            );
        }

        /* Con piu' settimane la scala torna larga e stabile: e' un confronto nel tempo,
           e un asse che si adatta a ogni cambio di periodo renderebbe due grafici
           affiancati non confrontabili. Il minimo scende sotto 50 solo se serve, per
           non tagliare fuori i punti bassi. */
        return { min: Math.max(0, Math.min(50, Math.floor(basso / 10) * 10)), max: 100 };
    }, [SETTIMANE, FASCE, punti, modo]);

    /* ----------------------------- grafici ----------------------------- */
    const optTrendFascia = useMemo(() => ({
        tooltip: { ...TOOLTIP, trigger: "axis", axisPointer: { type: "line", lineStyle: { color: "#94A3B8", width: 1 } },
            valueFormatter: (v) => `${nf(v, 1)}%` },
        legend: { ...LEGEND, top: 0, left: 0 },
        grid: { left: 44, right: 56, top: 40, bottom: 34 },
        xAxis: { ...asseSettimane, boundaryGap: false },
        yAxis: { type: "value", min: dominioTrend.min, max: dominioTrend.max, axisLine: { show: false }, splitLine: SPLIT_LINE,
            axisLabel: { ...AXIS_LABEL, formatter: "{value}%" } },
        series: FASCE.map((f, i) => ({
            name: titolo(f), type: "line", smooth: 0.25,
            // Punti pieni sulle settimane del periodo, piccoli su quelle di contesto.
            symbolSize: 9,
            lineStyle: { width: 2 }, itemStyle: { color: CAT[i], borderColor: "#fff", borderWidth: 2 },
            data: punti.map((p) => {
                const c = aggregato(SETTIMANE, "perFascia", [f], p.settimane)[f];
                return c && c[I_MPRO] ? Number(pct(fattoDi(c, I_MESE, modo), c[I_MPRO]).toFixed(1)) : null;
            }),
            endLabel: { show: true, distance: 6, fontSize: 11, fontWeight: 700, color: CAT[i], formatter: (p) => `${nf(p.value, 0)}%` },
        })),
    }), [SETTIMANE, FASCE, punti, asseSettimane, dominioTrend, modo]);

    const optQuartieri = useMemo(() => ({
        tooltip: { ...TOOLTIP, trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: (v) => `${nf(v, 1)}%` },
        legend: { ...LEGEND, top: 0, left: 0 },
        grid: { left: 44, right: 16, top: 40, bottom: 34 },
        xAxis: asseSettimane,
        // Solo recuperi: stessa scala corta del trend per fascia.
        yAxis: { type: "value", min: 0, max: modo === "solo" ? dominioTrend.max : 100, axisLine: { show: false }, splitLine: SPLIT_LINE,
            axisLabel: { ...AXIS_LABEL, formatter: "{value}%" } },
        series: QUARTIERI.map((q, i) => ({
            name: quartiere(q), type: "bar", barGap: "12%", barCategoryGap: "38%",
            itemStyle: { color: CAT[i], borderRadius: [4, 4, 0, 0] },
            data: punti.map((p) => {
                const c = aggregato(SETTIMANE, "perQuartiere", [q], p.settimane)[q];
                return c && c[I_MPRO] ? Number(pct(fattoDi(c, I_MESE, modo), c[I_MPRO]).toFixed(1)) : null;
            }),
        })),
    }), [SETTIMANE, QUARTIERI, punti, asseSettimane, modo, dominioTrend]);

    /* ------------------- etichette e valori nella lettura scelta ------------------- */
    const colFattoKm = modo === "solo" ? "Km recuperati" : "Km spazzati";
    const colFattoTr = modo === "solo" ? "Tratte recuperate" : "Tratte fatte";
    const colFattoKmBreve = modo === "solo" ? "Km recup." : "Km spazz.";
    const colFattoTrBreve = modo === "solo" ? "Recuperate" : "Fatte";
    const kmFatti = (c) => fattoDi(c, I_MESE, modo);
    const trFatte = (c) => fattoDi(c, I_ESE, modo);
    // Nel Totale la cella con recuperi ha la linguetta: classe da aggiungere a un <td>.
    const flagKm = (c) => (modo === "totale" && haRecuperi(c, I_MESE) ? " gp-rec-flag" : "");
    const flagTr = (c) => (modo === "totale" && haRecuperi(c, I_ESE) ? " gp-rec-flag" : "");
    /* Tooltip "prima -> dopo" di una copertura (Totale con recuperi), o undefined. */
    const primaDopo = (c, campoEse, campoPro) => {
        if (modo !== "totale" || !haRecuperi(c, campoEse)) return undefined;
        return `Prima del recupero ${nf(pct(c[campoEse] || 0, c[campoPro]), 1)}% → dopo ${nf(pct(fattoDi(c, campoEse, modo), c[campoPro]), 1)}%`;
    };
    /* Barra di avanzamento: nel Totale la parte recuperata e' un segmento rosso in coda
       a quella fatta nel percorso, cosi' si vede a colpo d'occhio quanto pesa. */
    const barra = (c, campoEse, campoPro, classe) => {
        const v = pct(fattoDi(c, campoEse, modo), c[campoPro]);
        const ese = pct(c[campoEse] || 0, c[campoPro]) || 0;
        const rec = pct(c[gemelloRec(campoEse)] || 0, c[campoPro]) || 0;
        return (
            <div className="prog" title={primaDopo(c, campoEse, campoPro)}>
                <div className="prog-track gp-prog-split">
                    {modo === "solo"
                        ? <div className="prog-fill gp-fill-rec" style={{ width: `${rec}%` }} />
                        : <>
                            <div className={`prog-fill ${classe}`} style={{ width: `${ese}%` }} />
                            {modo === "totale" && rec > 0 && <div className="prog-fill gp-fill-rec" style={{ width: `${rec}%` }} />}
                        </>}
                </div>
                <span className="prog-label">{nf(v, 1)}%</span>
            </div>
        );
    };

    /* ------------------------- blocchi riutilizzati ------------------------- */
    const badgeFascia = (f) => (
        <span className={`badge ${f === "NOTTE" ? "badge-info" : f === "MATTINA" ? "badge-warning" : "badge-neutral"}`}>
            {titolo(f)}
        </span>
    );

    // Tabella km/tratte usata sotto i due grafici: righe = fasce oppure quartieri.
    const TabellaBlocchi = ({ intestazione, voci, mappa, foto, etichetta }) => (
        <div className="gp-rep-subwrap">
            <div className="gp-rep-subcap">
                Valori del periodo filtrato: <strong>{etichettaPeriodo}</strong>
                {filtriAttivi && (
                    <em className="gp-rep-nofiltro"> · grafico e tabella restano su tutta la rete: i filtri non si applicano qui</em>
                )}
            </div>
            <div className="table-container gp-rep-subtable gp-rep-table">
                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>{intestazione}</th>
                                <th className="num gp-c gp-th-km">Km prev.</th>
                                <th className="num gp-c gp-th-km">{colFattoKmBreve}</th>
                                <th className="num gp-th-km">Cop. km</th>
                                <th className="num gp-c gp-th-tratte">Previste</th>
                                <th className="num gp-c gp-th-tratte">{colFattoTrBreve}</th>
                                <th className="num gp-th-tratte">Cop. tratte</th>
                                <th className="num gp-th-tratte" title="Tratte eseguite con almeno una foto, sul totale delle eseguite">Cop. foto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {voci.map((v) => {
                                const c = mappa[v] || CELLA_ZERO;
                                return (
                                    <tr key={v}>
                                        <td className="strong gp-rep-nowrap">{etichetta(v)}</td>
                                        <td className="num gp-c gp-td-km">{nf(km(c[I_MPRO]), 1)}</td>
                                        <td className="num gp-c gp-td-km">{nf(km(kmFatti(c)), 1)}</td>
                                        <td className={`num strong gp-td-km${flagKm(c)}`} title={primaDopo(c, I_MESE, I_MPRO)}>{pctTxt(kmFatti(c), c[I_MPRO])}</td>
                                        <td className="num gp-c gp-td-tratte">{nf(c[I_PRO])}</td>
                                        <td className="num gp-c gp-td-tratte">{nf(trFatte(c))}</td>
                                        <td className={`num strong gp-td-tratte${flagTr(c)}`} title={primaDopo(c, I_ESE, I_PRO)}>{pctTxt(trFatte(c), c[I_PRO])}</td>
                                        <td className="num strong gp-td-tratte"
                                            title={`${nf(foto[v] || 0)} tratte con almeno una foto, su ${nf(c[I_ESE])} eseguite`}>
                                            {pctTxt(foto[v] || 0, c[I_ESE])}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    /* Stessa forma delle card negli altri tab: due righe, etichetta e valore.
       Il dettaglio sta accanto al valore (non sotto) per non aggiungere una terza
       riga, e per esteso nel title. */
    return (
        <>
            <AvvisoEsecuzioni meta={meta} testoDanno={
                <>Le tratte previste e i chilometri programmati sono corretti; tutto ciò che riguarda
                <em> eseguito</em>, <em>copertura</em> e <em>foto</em> risulta a zero e non è attendibile.</>
            } />

            <div className="gp-toolbar-row">
                <button className={`toggle-filters-btn ${showFilters ? "active" : ""}`} onClick={() => setShowFilters((v) => !v)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                    {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                    <svg className="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                </button>

                {showFilters && (
                    <div className="filters-panel">
                        {/* Il periodo sta qui dentro come gli altri filtri: si compone e
                            si conferma con "Applica", non agisce a ogni tocco. */}
                        <SelettorePeriodo
                            mesi={MESI}
                            settimane={SETTIMANE}
                            anno={fAnno}
                            onAnno={cambiaAnno}
                            range={fRange}
                            onRange={cambiaRange}
                            settimaneSpente={fSpente}
                            onToggleSettimana={toggleSettimana}
                        />

                        <div className="filters-grid">
                            <div className="filter-group">
                                <label>Fascia</label>
                                <FiltroMulti placeholder="Tutte" options={FASCE.map((f) => ({ value: f, label: titolo(f) }))}
                                    value={fFascia} onChange={setFFascia} />
                            </div>
                            {conQuartieri && (
                                <div className="filter-group">
                                    <label>Quartiere</label>
                                    <FiltroMulti placeholder="Tutti" options={QUARTIERI.map((q) => ({ value: q, label: quartiere(q) }))}
                                        value={fQuartiere} onChange={setFQuartiere} />
                                </div>
                            )}
                            <div className="filter-group">
                                <label>Percorso</label>
                                <FiltroMulti placeholder="Tutti" options={opzioniDa(righeTutte.filter((r) => r.attiva).map((r) => r.cod))}
                                    value={fPercorso} onChange={setFPercorso} />
                            </div>
                        </div>

                        <div className="filter-actions">
                            <button className="btn btn-ghost" onClick={resetFiltri}>Reset</button>
                            <button className="btn btn-primary" onClick={applicaFiltri}>Applica</button>
                        </div>
                    </div>
                )}

            </div>

            {/* Riga di riepilogo: che periodo si sta guardando e quanto pesa.
                Il periodo si sceglie nel pannello, qui si legge soltanto. */}
            <div className="gp-rep-periodo">
                <span className="gp-rep-periodo-att">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {etichettaPeriodo}
                </span>
                {/* Lettura delle percentuali: stessi bottoni dei sotto-tab del tab Percorsi. */}
                <div className="gp-subtabs gp-rep-modi" role="group" aria-label="Lettura delle percentuali">
                    {MODI.map((m) => (
                        <button key={m.key} type="button" title={m.aiuto} aria-pressed={modo === m.key}
                            className={`gp-subtab ${modo === m.key ? "active" : ""}`} onClick={() => setModo(m.key)}>
                            {m.label}
                        </button>
                    ))}
                </div>
                <div className="spacer" />
                <button className="btn btn-ghost gp-new-btn" onClick={onAggiorna}
                    title="Ricalcola da Interacta e dall'app operatori (ignora la cache)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                    Aggiorna
                </button>
                <span className="results-badge">
                    {righe.length} percors{righe.length === 1 ? "o" : "i"} · {nf(totale[I_PRO])} tratt{totale[I_PRO] === 1 ? "a" : "e"} · {nf(km(totale[I_MPRO]), 1)} km previsti
                </span>
            </div>

            {/* ------------------------------ KPI ------------------------------ */}
            <div className="stats-grid gp-rep-kpi">
                {/* I chilometri vengono per primi: e' la misura su cui il cliente valuta un
                    percorso ("la percentuale va fatta nei chilometri, non in base alle vie").
                    Il blu e' anche il colore con cui i km sono gia' segnati nelle tabelle. */}
                {/* Nel Totale il sottotitolo dice quanto dei punti viene dai recuperi: e' la
                    linguetta rossa dei KPI. */}
                <KpiCard tone="blue" icona={ico(<path d="M4 19l4-14M20 19l-4-14M12 5v3m0 4v3m0 4v2" />)}
                    label={modo === "solo" ? "Km recuperati" : "Copertura km"}
                    valore={pctTxt(kmFatti(totale), totale[I_MPRO])}
                    sub={modo === "totale" && haRecuperi(totale, I_MESE)
                        ? `di cui +${nf(pct(totale[I_MREC], totale[I_MPRO]), 1)} pt da recuperi`
                        : `su ${nf(km(totale[I_MPRO]), 0)} km`}
                    titolo={modo === "totale" && haRecuperi(totale, I_MESE)
                        ? `${nf(km(kmFatti(totale)), 1)} km su ${nf(km(totale[I_MPRO]), 1)} previsti, di cui ${nf(km(totale[I_MREC]), 1)} recuperati. ${primaDopo(totale, I_MESE, I_MPRO)}.`
                        : `${nf(km(kmFatti(totale)), 1)} km ${modo === "solo" ? "recuperati" : "spazzati"} su ${nf(km(totale[I_MPRO]), 1)} previsti`} />
                <KpiCard tone="slate" icona={ico(<path d="M20 6L9 17l-5-5" />)}
                    label={modo === "solo" ? "Tratte recuperate" : "Copertura tratte"}
                    valore={pctTxt(trFatte(totale), totale[I_PRO])}
                    sub={modo === "totale" && haRecuperi(totale, I_ESE)
                        ? `${nf(trFatte(totale))}/${nf(totale[I_PRO])} · ${nf(totale[I_REC])} recuperate`
                        : `${nf(trFatte(totale))}/${nf(totale[I_PRO])}`}
                    titolo={modo === "totale" && haRecuperi(totale, I_ESE)
                        ? `${nf(trFatte(totale))} tratte su ${nf(totale[I_PRO])} previste, di cui ${nf(totale[I_REC])} recuperate. ${primaDopo(totale, I_ESE, I_PRO)}.`
                        : `${nf(trFatte(totale))} tratte ${modo === "solo" ? "recuperate" : "eseguite"} su ${nf(totale[I_PRO])} previste`} />
                <KpiCard tone="green" label="Copertura foto"
                    icona={ico(<><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>)}
                    valore={pctTxt(conFotoTot, totale[I_ESE])}
                    sub={`${nf(conFotoTot)}/${nf(totale[I_ESE])}`}
                    titolo={`${nf(conFotoTot)} tratte eseguite con almeno una foto, su ${nf(totale[I_ESE])}`} />
                {/* Il conteggio resta qui come colpo d'occhio; il dettaglio vive nel tab
                    "Tratte da recuperare". La stella e' la stessa che le segna li'. */}
                <KpiCard tone="red" label="Tratte da recuperare" valore={nf(CRITICHE_TOT)}
                    icona={(
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                    )}
                    sub={`${CRITICHE_ROSSE} rosse · al ${formatData(meta.aggiornatoAl)}`}
                    titolo={`Tratte previste e non eseguite all’ultimo passaggio utile (stato al ${formatData(meta.aggiornatoAl)}). ${CRITICHE_ROSSE} hanno due o più mancate consecutive. Il dettaglio è nel tab "Tratte da recuperare".`} />
            </div>

            {/* --------------------------- LE DUE MATRICI --------------------------- */}
            <div className={`gp-rep-matrici ${fsMatrici ? "gp-rep-fullscreen" : ""}`}>
                <RotateDevice active={fsMatrici} maxWidth={1024} />
                <div className="gp-rep-matrici-bar">
                    <div className="gp-rep-title">Matrice copertura — percorso × giorno</div>
                    <div className="spacer" />
                    {(hl.riga !== null || hl.colonna !== null) && (
                        <button className="btn btn-ghost gp-rep-clear" onClick={() => setHl({ riga: null, colonna: null })}>
                            Togli evidenza{hl.riga ? ` · ${hl.riga}` : ""}{hl.colonna ? ` · ${hl.colonna}` : ""}
                        </button>
                    )}
                    <ExportBtn titolo="Esporta le due matrici in Excel" onExport={() => esporta("matrici", righe)} />
                    <FullscreenBtn attivo={fsMatrici} onClick={() => setFsMatrici((v) => !v)} />
                </div>
                <div className="gp-rep-matrici-grid">
                    <Matrice
                        titolo="Chilometri"
                        sottotitolo={modo === "solo" ? "Percentuale di chilometri recuperati sui chilometri previsti."
                            : "Percentuale di chilometri spazzati sui chilometri previsti."}
                        righe={righe} giorni={GIORNI} campoPro={I_MPRO} campoEse={I_MESE} unita="km"
                        hl={hl} onHl={setHl} modo={modo}
                    />
                    <Matrice
                        titolo="Tratte"
                        sottotitolo={modo === "solo" ? "Percentuale di tratte recuperate sulle tratte previste."
                            : "Percentuale di tratte eseguite sulle tratte previste."}
                        righe={righe} giorni={GIORNI} campoPro={I_PRO} campoEse={I_ESE} unita="tratte"
                        hl={hl} onHl={setHl} modo={modo}
                    />
                </div>
                <div className="gp-rep-legenda">
                    <span className="gp-rep-legenda-label">{modo === "solo" ? "Recuperato" : "Copertura"}</span>
                    {modo === "solo" ? (
                        <div className="gp-rep-legenda-scala">
                            <span className="gp-rep-legenda-barra" style={{ background: LEGENDA_SOLO }} />
                            {[0, 10, 20].map((t) => (
                                <span className="gp-rep-legenda-tick" style={{ left: `${(t / PIENO_SOLO) * 100}%` }} key={t}>{t}%</span>
                            ))}
                            <span className="gp-rep-legenda-tick" style={{ left: "100%" }}>{PIENO_SOLO}%+</span>
                        </div>
                    ) : (
                        <div className="gp-rep-legenda-scala">
                            <span className="gp-rep-legenda-barra" style={{ background: LEGENDA_GRADIENTE }} />
                            {[0, 70, 90, 100].map((t) => (
                                <span className="gp-rep-legenda-tick" style={{ left: `${t}%` }} key={t}>{t}%</span>
                            ))}
                        </div>
                    )}
                    {modo === "totale" && (
                        <span className="gp-rep-legenda-rec">
                            <span className="gp-rec-flag gp-rep-legenda-campione" aria-hidden="true" />
                            Include recuperi: passa sopra la cella per il valore prima del recupero
                        </span>
                    )}
                    <span className="sub gp-rep-legenda-nota">Clicca un giorno o un percorso per evidenziarlo in entrambe le matrici.</span>
                </div>
            </div>

            {/* ---------------------- TABELLA DETTAGLIO PERCORSO ---------------------- */}
            <div className={`table-container gp-rep-table ${fsTabella ? "gp-fullscreen-table" : ""}`}>
                <RotateDevice active={fsTabella} maxWidth={1024} />
                <div className="gp-table-topbar">
                    <div className="gp-rep-title" style={{ marginRight: "auto" }}>Dettaglio per percorso</div>
                    <ExportBtn titolo="Esporta il dettaglio in Excel" onExport={() => esporta("dettaglio", righe)} />
                    <FullscreenBtn attivo={fsTabella} onClick={() => setFsTabella((v) => !v)} />
                </div>
                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th className="gp-c">Percorso</th>
                                <th>Fascia</th>
                                <th className="gp-c">Quartiere</th>
                                <th className="num gp-c gp-th-km">Km previsti</th>
                                <th className="num gp-c gp-th-km">{colFattoKm}</th>
                                <th className="num gp-th-km">Copertura km</th>
                                <th className="num gp-c gp-th-tratte">Tratte previste</th>
                                <th className="num gp-c gp-th-tratte">{colFattoTr}</th>
                                <th className="num gp-th-tratte">Copertura tratte</th>
                                <th className="num">Foto</th>
                                <th title={`Copertura in chilometri sul periodo scelto: ${passoTxt}.`}>Trend</th>
                            </tr>
                        </thead>
                        <tbody>
                            {righe.map((r) => {
                                return (
                                    <tr key={r.cod}>
                                        <td className="gp-c"><span className="codice">{r.cod}</span></td>
                                        <td>{badgeFascia(r.fascia)}</td>
                                        <td className="sub gp-c gp-rep-nowrap">{quartiere(r.quartiere)}</td>
                                        <td className="num gp-c gp-td-km">{nf(km(r.tot[I_MPRO]), 1)}</td>
                                        <td className="num gp-c gp-td-km">{nf(km(kmFatti(r.tot)), 1)}</td>
                                        <td className={`num gp-td-km${flagKm(r.tot)}`}>
                                            {barra(r.tot, I_MESE, I_MPRO, "gp-fill-km")}
                                        </td>
                                        <td className="num gp-c gp-td-tratte">{nf(r.tot[I_PRO])}</td>
                                        <td className="num gp-c gp-td-tratte">{nf(trFatte(r.tot))}</td>
                                        <td className={`num gp-td-tratte${flagTr(r.tot)}`}>
                                            {barra(r.tot, I_ESE, I_PRO, "gp-fill-tratte")}
                                        </td>
                                        <td className="num">
                                            {nf(r.conFoto)} <span className="sub">({pctTxt(r.conFoto, r.tot[I_ESE], 0)})</span>
                                        </td>
                                        <td><Sparkline valori={trendPerCodifica[r.cod] || []} min={minSpark} /></td>
                                    </tr>
                                );
                            })}
                            {righe.length === 0 && (
                                <tr><td colSpan={11}><div className="gp-empty">Nessun percorso con questi filtri.</div></td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={3}>Totale · {righe.length} percorsi</td>
                                <td className="num gp-c gp-td-km">{nf(km(totale[I_MPRO]), 1)}</td>
                                <td className="num gp-c gp-td-km">{nf(km(kmFatti(totale)), 1)}</td>
                                <td className={`num gp-td-km${flagKm(totale)}`} title={primaDopo(totale, I_MESE, I_MPRO)}>{pctTxt(kmFatti(totale), totale[I_MPRO])}</td>
                                <td className="num gp-c gp-td-tratte">{nf(totale[I_PRO])}</td>
                                <td className="num gp-c gp-td-tratte">{nf(trFatte(totale))}</td>
                                <td className={`num gp-td-tratte${flagTr(totale)}`} title={primaDopo(totale, I_ESE, I_PRO)}>{pctTxt(trFatte(totale), totale[I_PRO])}</td>
                                <td className="num">{nf(conFotoTot)}</td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* ------------------ TREND FASCIA + QUARTIERI, con tabelle ------------------ */}
            <div className="gp-rep-duo">
                <div className="gp-rep-col">
                    <div className="gp-rep-card">
                        <div className="gp-rep-card-head">
                            <div>
                                <div className="gp-rep-title">Trend copertura per fascia</div>
                                <div className="gp-rep-sub">{modo === "solo" ? "Chilometri recuperati" : "Copertura chilometri"} sul periodo scelto: {passoTxt}.</div>
                            </div>
                        </div>
                        <ReactECharts option={optTrendFascia} style={{ height: "310px" }} notMerge />
                    </div>
                    <TabellaBlocchi intestazione="Fascia" voci={FASCE} mappa={perFascia} foto={fotoFascia} etichetta={titolo} />
                </div>
                {/* Il quartiere non e' un campo dell'anagrafica tratti su Interacta: l'abbiamo
                    popolata noi da un file del cliente che non lo conteneva. Finche' non
                    arriva il file aggiornato, il blocco resta al suo posto ma dichiarato
                    non disponibile: quando `quartieri` si popolera' lato backend, si
                    riaccende da solo senza rimettere mano a questa pagina. */}
                <div className="gp-rep-col">
                    {conQuartieri ? (
                        <>
                            <div className="gp-rep-card">
                                <div className="gp-rep-card-head">
                                    <div>
                                        <div className="gp-rep-title">Copertura per quartiere</div>
                                        <div className="gp-rep-sub">{modo === "solo" ? "Chilometri recuperati" : "Copertura chilometri"} sul periodo scelto: {passoTxt}.</div>
                                    </div>
                                </div>
                                <ReactECharts option={optQuartieri} style={{ height: "310px" }} notMerge />
                            </div>
                            <TabellaBlocchi intestazione="Quartiere" voci={QUARTIERI} mappa={perQuartiere} foto={fotoQuartiere} etichetta={quartiere} />
                        </>
                    ) : (
                        <div className="gp-rep-card gp-rep-nodata">
                            <div className="gp-rep-card-head">
                                <div>
                                    <div className="gp-rep-title">Copertura per quartiere</div>
                                    <div className="gp-rep-sub">Dato non disponibile.</div>
                                </div>
                            </div>
                            <div className="gp-rep-nodata-body">
                                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                                </svg>
                                <p>Il quartiere non è presente nell’anagrafica delle tratte.</p>
                                <p className="sub">In attesa dell’anagrafica aggiornata dal cliente: appena arriva, questo grafico e la sua tabella si popolano da soli.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

        </>
    );
};

/**
 * Il guscio: carica il report e decide cosa mostrare prima che i dati ci siano.
 *
 * Il tab e' montato solo quando e' selezionato (GestionePercorsi.js), quindi la
 * chiamata parte all'apertura e non pesa sugli altri tre. Un solo `loading` per
 * tutta la pagina: i grafici dipendono tutti dallo stesso payload, spezzettarlo
 * darebbe soltanto uno sfarfallio.
 */
const TabReportistica = ({ zona }) => {
    const [dati, setDati] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState(null);

    const carica = useCallback(async ({ refresh = false } = {}) => {
        setLoading(true);
        setErrore(null);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/report`, {
                headers: authHeaders(),
                params: { zona, refresh: refresh ? 1 : undefined },
            });
            setDati(res.data);
        } catch (e) {
            console.error("Errore caricamento reportistica:", e);
            setErrore(e?.response?.data?.error || "Non è stato possibile caricare la reportistica.");
            setDati(null);
        } finally {
            setLoading(false);
        }
    }, [zona]);

    useEffect(() => { carica(); }, [carica]);

    if (loading) {
        return (
            <div className="gp-loading">
                <Lf23Loader testo="Calcolo della reportistica in corso..." />
            </div>
        );
    }

    if (errore) {
        return (
            <div className="gp-empty">
                <p>{errore}</p>
                <button className="btn btn-ghost" onClick={() => carica()}>Riprova</button>
            </div>
        );
    }

    /* Storico vuoto: nessun percorso concluso da cui ricavare un periodo. Meglio dirlo
       che mostrare una pagina di zeri, che sembrerebbe funzionante. */
    if (!dati || !dati.mesi.length) {
        return (
            <div className="gp-empty">
                <p>Nessun percorso concluso da cui calcolare la reportistica.</p>
                <p>La pagina si popola da sola quando i percorsi passano a uno stato esecutivo.</p>
            </div>
        );
    }

    return <Reportistica dati={dati} onAggiorna={() => carica({ refresh: true })} />;
};

export default TabReportistica;
